import axios from "axios";

/** Shared axios instance for every backend call (expert app and client dashboard). */
export const api = axios.create({
  baseURL: import.meta.env.VITE_BE_BASE_URL + "pid-kks-extractor",
});

let backendErrorHandler: ((message: string) => void) | null = null;

export function setBackendErrorHandler(
  handler: ((message: string) => void) | null,
) {
  backendErrorHandler = handler;
}

/** Surfaces a client-side failure through the same popup as backend errors. */
export function reportBackendError(message: string) {
  if (backendErrorHandler) backendErrorHandler(message);
}

function extractBackendErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return "Unexpected error while contacting backend.";
  }

  const status = error.response?.status;
  const payload = error.response?.data as
    | string
    | { detail?: unknown; message?: string }
    | undefined;

  if (typeof payload === "string" && payload.trim()) {
    return status ? `${status}: ${payload}` : payload;
  }

  const rawDetail =
    typeof payload === "object" && payload
      ? (payload.detail ?? payload.message)
      : undefined;

  const detail =
    rawDetail !== null &&
    typeof rawDetail === "object" &&
    "message" in rawDetail
      ? String((rawDetail as { message: unknown }).message)
      : typeof rawDetail === "string"
        ? rawDetail
        : undefined;

  if (detail) {
    return status ? `${status}: ${detail}` : detail;
  }

  if (status) {
    return `Backend request failed with status ${status}.`;
  }

  return error.message || "Backend request failed.";
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (backendErrorHandler) {
      backendErrorHandler(extractBackendErrorMessage(error));
    }
    return Promise.reject(error);
  },
);

/** Set the Bearer token for all API requests. Called once the auth token is known. */
export function setAuthToken(token: string | null) {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}
